// Plans, inside the app: the same pricing the public page shows, plus what this
// workspace is using and the way to ask for an upgrade.
//
// There is no checkout. No payment provider is wired (ADR-0009), so choosing a
// plan records a request an operator settles offline — the shape of it is in
// migration 20260823000300.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { currentWorkspace } from "../lib/api";
import { BILLING_PERMISSION, BILLING_ROUTE } from "../billing";
import { useWorkspace } from "../lib/WorkspaceContext";
import { featureCell, LifetimeExplainer, PricingTable } from "../components/PricingTable";
import { guessCountry, loadEntitlements, pricingCatalog, regionFor } from "../lib/entitlements";
import {
  isOpen, listPlanChangeRequests, type PlanChangeRequest,
} from "../lib/billing";
import { logError } from "../lib/logger";
import type { Entitlements, PricingCatalog } from "../lib/types";

export function Plans() {
  const { can } = useWorkspace();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [requests, setRequests] = useState<PlanChangeRequest[]>([]);
  const [region, setRegion] = useState<string | null>(null);
  const [yearly, setYearly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async (ws: string) => {
    setRequests(await listPlanChangeRequests(ws).catch(() => []));
    setEnt(await loadEntitlements(ws).catch(() => null));
  };

  useEffect(() => {
    (async () => {
      const [cat, ws] = await Promise.all([pricingCatalog(), currentWorkspace()]);
      setCatalog(cat);
      setRegion(regionFor(cat.regions, guessCountry())?.code ?? null);
      await reload(ws);
    })().catch((e) => {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "load_plans" });
    });
  }, []);

  const currentCode = ent?.plan?.code ?? "free";
  const open = requests.find(isOpen) ?? null;

  if (error) return <div className="card err">{error}</div>;
  if (!catalog || !region) return <p className="muted">Loading plans…</p>;

  return (
    <>
      <h1>Plans</h1>
      <p className="muted" style={{ maxWidth: "52ch", marginTop: "-.4rem" }}>
        Publish to every network from one place. Upgrade or change plan whenever you like.
      </p>

      {open && (
        <div className="card">
          <div className="notice upgrade">
            Your request to move to <strong>{open.to_plan}</strong> is being reviewed.{" "}
            <Link to="/profile">See its status</Link>
          </div>
        </div>
      )}

      <PricingTable
        catalog={catalog}
        region={region}
        onRegion={setRegion}
        yearly={yearly}
        onYearly={setYearly}
        currentPlan={currentCode}
        onChoose={(plan, interval) => navigate(`/plan-selection?${new URLSearchParams({ plan, interval, region })}`)}
        disabledReason={open ? "You already have a request being reviewed. Cancel it from your profile to ask for something else." : null}
      />

      {/* Usage used to be rendered here, to everybody. It moved to /billing,
          which is gated on `billing.view` — how much of the plan a workspace
          has spent, and which colleague spent it, is not something a creator
          should read on the way to seeing what a Pro plan costs. What is left
          here is the pointer, and only for the people who may follow it. */}
      {can(BILLING_PERMISSION) && (
        <div className="card">
          <h2>Your usage</h2>
          <p className="muted">
            What this workspace has spent against its plan — posts, AI credits, connected
            accounts, storage and transfer — is on{" "}
            <Link to={BILLING_ROUTE}>Billing &amp; usage</Link>.
          </p>
        </div>
      )}

      <div className="card">
        <h2>Compare every feature</h2>
        <div className="table-scroll">
          <table className="plans">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                {catalog.plans.map((p) => (
                  <th scope="col" key={p.code} className={p.code === currentCode ? "current" : ""}>
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {catalog.features.map((f) => (
                <tr key={f.key}>
                  <th scope="row">
                    {f.name}
                    {/* Sold and modelled, not built yet — saying so beats a support ticket. */}
                    {!f.is_implemented && <span className="soon">coming soon</span>}
                    {f.description && <span className="muted desc">{f.description}</span>}
                  </th>
                  {catalog.plans.map((p) => (
                    <td key={p.code} className={p.code === currentCode ? "current" : ""}>
                      {featureCell(catalog, p.code, f.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LifetimeExplainer catalog={catalog} />
    </>
  );
}
